import React, { useContext } from 'react'
import { FormFields } from '~/components/FormFields'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Checkbox, Form } from 'semantic-ui-react'
import { ClimaOptionsContext } from '../..'
import { OptionContent } from '../../style'
import PlantSelection from './PlantSelection'
import InfoPopup from '~/components/InfoPopup'
import styled from 'styled-components'
import DieselConsumptionTable from './DieselConsumptionTable'

const StyledLabel = styled.label`
  display: -webkit-box !important;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledDieselLabel = styled.div`
 a {
  color: #4183c4 !important;
 }
`

const FourthStep:React.FC = () => {
  const{ control, setValue, clearErrors } = useFormContext()
  const { irrigationMethods } = useContext(ClimaOptionsContext)

  const watchDieselConsumption = useWatch({ control, name: 'grassSilage.dieselConsumption' })
  const watchIrrigation = useWatch({ control, name: 'grassSilage.irrigation' })
  const watchIrrigationMethod = useWatch({ control, name: 'grassSilage.irrigationMethod' })

  function renderDieselInput() {
    return <FormFields.Input
      noErrorMessage
      label={'Diesel'}
      typeInput='controled'
      style={{ marginTop: 16 }}
      onChange={(event) => {
        setValue('grassSilage.irrigationDieselUsed', event?.target?.value)
        clearErrors('grassSilage.irrigationDieselUsed')
      }}
      suffix=' l/ha'
      placeholder={'in Liter pro Hektar'}
      name='grassSilage.irrigationDieselUsed'
      control={control}
    />
  }

  function renderStormInput() {
    return <FormFields.Input
      noErrorMessage
      label={'Strom'}
      typeInput='controled'
      style={{ marginTop: 16 }}
      onChange={(event) => {
        setValue('grassSilage.irrigationElectricityUsed', event?.target?.value?.replace('kWh', ''))
        clearErrors('grassSilage.irrigationElectricityUsed')
      }}
      suffix=' kWh'
      placeholder={'in kWh'}
      name='grassSilage.irrigationElectricityUsed'
      control={control}
    />
  }

  function renderIrigationMethodInputs() {
    switch(watchIrrigationMethod) {
      case 1:
        return renderDieselInput()
      case 2:
        return renderStormInput()
      case 3:
        return <>
          {renderDieselInput()}
          {renderStormInput()}
        </>
      default:
        return <></>
    }
  }

  return <>
    <OptionContent>
      <Form.Group widths={'equal'} style={{ alignItems: 'end' }}>
        <FormFields.Input
          noErrorMessage
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>
              <StyledDieselLabel dangerouslySetInnerHTML={{ __html: `Bitte geben Sie den jährlichen Dieselverbrauch für den Futterbau an. Nutzen Sie möglichst eigene Aufzeichnungen (z. B. Tankdaten), Abrechnungen von Lohnunternehmern oder Telematikdaten. 
            Falls keine genauen Daten vorliegen, können Sie sich an den Richtwerten in der Tabelle orientieren. Dies sind lediglich Orientierungswerte! 
            Genauere und mehr Werte finden Sie im Internet auf der Seite des KTBL-Dieselbedarf-Rechners: <a target="_blank" href="https://daten.ktbl.de/dieselbedarf/home.html">KTBL-Dieselbedarf</a>` }} />
            </label>
            <InfoPopup
              content={<DieselConsumptionTable/>}
              hoverable={true}
              position={'right center'}
              backgroundTransparent
              styleIcon={{ fontSize: '1.2em' }}
            />
          </div>}
          typeInput='controled'
          suffix=' l/ha'
          onChange={(event) => {
            setValue('grassSilage.dieselConsumption', event?.target?.value?.replace('l/ha', ''))
            clearErrors('grassSilage.dieselConsumption')
          }}
          placeholder={'in l/ha'}
          name='grassSilage.dieselConsumption'
          control={control}
        />
        {Boolean(watchDieselConsumption) && <FormFields.Input
          noErrorMessage
          label={'Wie hoch war der durchschnittliche Anteil Biodiesel?'}
          typeInput='controled'
          onChange={(event) => {
            setValue('grassSilage.biodieselPercentage', event?.target?.value?.replace('%', ''))
            clearErrors('grassSilage.biodieselPercentage')
          }}
          suffix=' %'
          placeholder={'in %'}
          name='grassSilage.biodieselPercentage'
          control={control}
        />}
      </Form.Group>
      <Form.Group widths={'equal'}>
        <Controller
          name='grassSilage.irrigation'
          render={({ fieldState: { error } }) => (
            <Form.Field error={Boolean(error)}>
              <label>{'Haben Sie Ihr Grünland bewässert?'}</label>
              <div style={{ display: 'flex', gap: 20 }}>
                <Checkbox
                  radio
                  label='Ja'
                  checked={watchIrrigation === 1}
                  onClick={() => {
                    setValue('grassSilage.irrigation', 1)
                    clearErrors('grassSilage.irrigation')
                  }}
                />
                <Checkbox
                  radio
                  label='Nein'
                  checked={watchIrrigation === 0}
                  onClick={() => {
                    setValue('grassSilage.irrigation', 0)
                    clearErrors('grassSilage.irrigation')
                  }}
                />
              </div>
            </Form.Field>
          )}
        />
        {Boolean(watchIrrigation) && <>
          <FormFields.Input
            noErrorMessage
            label={'Wie viel Wasser wurde zur Bewässerung verbraucht?'}
            typeInput='controled'
            onChange={(event) => {
              setValue('grassSilage.irrigationWaterUsed', event?.target?.value?.replace('l/ha', ''))
              clearErrors('grassSilage.irrigationWaterUsed')
            }}
            suffix=' l/ha'
            placeholder={'in l/ha'}
            name='grassSilage.irrigationWaterUsed'
            control={control}
          />
          <FormFields.Select
            control={control}
            noErrorMessage
            clearable
            onChange={(e, data) => {
              setValue('grassSilage.irrigationMethod', data.value)
              clearErrors('grassSilage.irrigationMethod')
            }}
            label={<StyledLabel>{'Welches Bewässerungsverfahren wurde angewandt?'}</StyledLabel>}
            placeholder={'Art des Bewässerungsverfahren'}
            name='grassSilage.irrigationMethod'
            options={irrigationMethods?.map(irrigationMethod => ({ value: irrigationMethod.key, text: irrigationMethod.label }))}
          />
          {watchIrrigationMethod && renderIrigationMethodInputs()}
        </>}
      </Form.Group>
      <PlantSelection/>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          noErrorMessage
          label={'Wie viel Saatgut wurde eingesetzt?'}
          typeInput='controled'
          onChange={(event) => {
            setValue('grassSilage.seedUsed', event?.target?.value?.replace('kg/ha', ''))
            clearErrors('grassSilage.seedUsed')
          }}
          suffix=' kg/ha'
          placeholder={'in kg/ha'}
          name='grassSilage.seedUsed'
          control={control}
        />
        {/*      <FormFields.Input
          noErrorMessage
          label={'Wie viel Folie wurde eingesetzt?'}
          typeInput='controled'
          onChange={(event) => {
            setValue('grassSilage.filmUsed', event?.target?.value?.replace('m²', ''))
            clearErrors('grassSilage.filmUsed')
          }}
          suffix=' m²'
          placeholder={'in m²'}
          name='grassSilage.filmUsed'
          control={control}
        /> */}
      </Form.Group>
    </OptionContent>
  </>
}
export default FourthStep